<template>
  <div :style="style.container">
    <div :style="style.headerContainer">
      <SelectSecondary
        v-bind="attrs.select"
        :initSelectedOptionIndex="optionIndex"
        :options="options"
        @select="selectOption"
      >
        <template v-slot:select>
          {{ options[optionIndex].label }}
        </template>
      </SelectSecondary>
    </div>
    <div :style="style.subHeader">
      {{ subHeaderText }}
    </div>
    <CardPrimary :style="style.card">
      <template v-if="isLoading">
        <LoaderPrimary
          :loading="true"
          :style="style.loader"
        />
      </template>
      <template v-else>
        <div
          v-for="(skillNode, index) in selectedSkillNodes"
          :key="index"
        >
          <SkillListRow
            :skillNode="skillNode"
            :viewOnly="viewOnly"
            @mark-as-required="addRequiredSkill"
            @select="selectSkill"
            @unmark-as-required="deleteRequiredSkill"
            @unselect="unselectSkill"
          />
          <div :style="style.divider" />
        </div>
      </template>
    </CardPrimary>
  </div>
</template>

<script>
  import ApiTenantTeamSkill from '@js/api/tenant-team-skill'
  import CardPrimary from '@modules/card/card-primary'
  import LoaderPrimary from '@modules/loader/loader-primary'
  import SelectSecondary from '@modules/select/select-secondary'
  import SkillListRow from '@teamup/modules/skill-list-row'
  import { FontStyle, Layout } from '@js/styles'
  import { mapGetters } from 'vuex'

  const tabPrimaryHeight = '21px';
  const teamSkillListHeaderHeight = '68px';
  const teamSkillListSubHeaderHeight = '24px';

  export default {
    name: 'SkillList',
    components: {
      CardPrimary,
      LoaderPrimary,
      SelectSecondary,
      SkillListRow,
    },
    props: {
      isLoading: {
        type: Boolean,
        required: false,
        default: false,
      },
      skillNodes: {
        type: Array,
        required: false,
        default: () => [],
      },
      tenantTeamId: {
        type: Number,
        required: false,
        default: () => null,
      },
      viewOnly: {
        type: Boolean,
        required: false,
        default: true,
      },
      visibleTenantUsers: {
        type: Array,
        required: false,
        default: () => [],
      },
    },
    data: function () {
      return {
        optionIndex: 0,
        attrs: {
          select: {
            minWidth: '240px',
          },
        },
        style: {
          card: {
            height: `calc(100% - ${teamSkillListHeaderHeight} - ${teamSkillListSubHeaderHeight})`,
            overflowY: 'scroll',
            width: '100%',
          },
          container: {
            height: `calc(100% - ${tabPrimaryHeight})`,
            width: '100%',
          },
          divider: {
            height: '1px',
            width: '100%',
          },
          headerContainer: {
            alignItems: 'center',
            display: 'flex',
            height: teamSkillListHeaderHeight,
            justifyContent: 'space-between',
            padding: `${Layout.space.md} 0`,
            width: '100%',
          },
          loader: {
            height: '100%',
            width: '100%',
          },
          subHeader: {
            fontSize: FontStyle.size.sm,
            fontWeight: FontStyle.weight.lg,
            height: Layout.space.md,
            margin: `0 0 ${Layout.space.sm} 0`,
          },
        },
      };
    },
    created: function () {
      this.style.divider = {
        ...this.style.divider,
        backgroundColor: this.color.dark5(),
      };
    },
    computed: {
      ...mapGetters('colorTheme', ['color']),
      noUserIsSelected: function () {
        return this.selectedTenantUserId == null;
      },
      options: function () {
        return [
          { label: 'All shown members', tenantTeamTenantUserId: null, tenantUserId: null },
          ...(this.visibleTenantUsers.reduce((result, elem) => {
                result.push({
                  label: `${elem.user.firstName} ${elem.user.lastName}`,
                  tenantUserId: elem.id,
                });
                return result;
              }, [])),
        ];
      },
      selectedSkillNodes: function () {
        return this.selectedTenantUserId == null ? this.skillNodes : (
          this.skillNodes.filter(elem => elem.tenantUserIds.includes(this.selectedTenantUserId)));
      },
      selectedTenantUserId: function () {
        return this.optionIndex === 0 ? null : this.options[this.optionIndex].tenantUserId;
      },
      subHeaderText: function () {
        const length = this.selectedSkillNodes.filter(elem => elem.visible).length;
        return this.noUserIsSelected ? `Total shown skills: ${length}` : `Shown skills: ${length}`;
      },
    },
    methods: {
      addRequiredSkill: function (skillNode) {
        const skill = {
          id: skillNode.id,
          name: skillNode.name,
        };
        this.$emit('add-required-skill', skill);
      },
      deleteRequiredSkill: function (skillNode) {
        const requiredSkill = {
          id: skillNode.id,
          name: skillNode.name,
        };
        this.$emit('delete-required-skill', requiredSkill);
      },
      selectOption: function (index) {
        this.optionIndex = index;
        this.$emit('select', index);
      },
      selectSkill: async function (skillNode) {
        const skill = {
          id: skillNode.id,
          name: skillNode.name,
        };
        this.$emit('selected', skill);
      },
      unselectSkill: async function (skillNode) {
        this.$emit('unselected', skillNode.tenantTeamSkillId);
      },
    },
  };
</script>
